import type { OpenAPI } from "simplydo/interfaces";

export type CalendarQueryData =
  OpenAPI.GET<"/calendars/{calendar_id}/calendar">["response"];

export type CalendarType = NonNullable<CalendarQueryData["calendar"]>;
export type CalendarResponseEvent = NonNullable<CalendarType["events"]>[number];

export type ChallengeCalendarEvent = CalendarResponseEvent;

export type CalendarEventMember = {
  _id?: string;
  profile?: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    jobTitle?: string | null;
    department?: string | null;
  };
};

export type CalendarWithDates = Omit<CalendarType, "start" | "end"> & {
  start: Date;
  end: Date;
};

export type CalendarEventWithDates = Omit<
  CalendarResponseEvent,
  "from" | "to" | "type" | "ownerMembers"
> & {
  from: Date;
  to?: Date;
  type?: CalendarResponseEvent["type"] | "meeting" | "reminder";
  ownerMembers?: CalendarEventMember[];
  organiserName?: string;
  organiserEmail?: string;
};

export const toEditingEvent = (
  event: CalendarResponseEvent,
): Partial<CalendarEventWithDates> => ({
  ...event,
  from: new Date(event.from),
  to: event.to ? new Date(event.to) : undefined,
});

export type CalendarEditingEvent = Partial<
  CalendarEventWithDates & { eventContent?: OpenAPI.Schemas["Announcement"] }
>;

export type CreateCalendarEventBody = Omit<
  OpenAPI.POST<"/calendars/{calendar_id}/calendar/events">["requestBody"],
  "notifications"
> & { notifications?: NonNullable<CalendarResponseEvent["notifications"]> };

export type CalendarEventUpdateBody = NonNullable<
  OpenAPI.POST<"/calendars/{calendar_id}/calendar/events/{event_id}">["requestBody"]
>;

export type CalendarEventUpdateSource = Partial<
  Pick<
    CalendarResponseEvent,
    | "title"
    | "description"
    | "allDay"
    | "color"
    | "members"
    | "notifications"
    | "organiserName"
    | "organiserEmail"
  >
> & { from?: string | Date | null; to?: string | Date | null };

export const toCalendarEventUpdateBody = (
  event: CalendarEventUpdateSource,
  notifyMembers: boolean,
): CalendarEventUpdateBody => ({
  title: event.title,
  description: event.description,
  from: event.from ? new Date(event.from).toISOString() : undefined,
  to: event.to ? new Date(event.to).toISOString() : undefined,
  allDay: event.allDay,
  color: event.color,
  members: event.members,
  notifications: event.notifications,
  notifyMembers,
  organiserName: event.organiserName,
  organiserEmail: event.organiserEmail,
});

/** A draft event a day cell action opens the editor with. */
export type CellActionEvent = Omit<
  Partial<CalendarResponseEvent>,
  "eventContent"
> & {
  eventContent?: Partial<NonNullable<CalendarResponseEvent["eventContent"]>>;
};

export type CellAction = {
  name: string;
  onlyVisibleInRange?: boolean;
  action: (dayDate: string) => CellActionEvent;
};

export const isEditableCalendarEventType = (type?: string) =>
  type === "event" || type === "meeting" || type === "reminder";
